'use client';

import { useDataAnalytics } from '@/hooks/analytics/analytics';
import { dataTestTransformer } from '@/utils/data-test-helper';
import { Attribute, Theater } from '@cinemark/content-api-client';
import clsx from 'clsx';
import Image from 'next/image';
import { TheaterAmenityDialog } from './theater-amenity-dialog';
import theaterAmenityStyles from './theater-amenity.module.scss';

type TheaterAmenityComponentProps = {
  attribute: Attribute;
  className?: string;
  dataLinkCategory: string;
  size?: TheaterAmenitySize;
  textClassName?: string;
  theater: Theater;
};

export type TheaterAmenitySize = 'sm' | 'md' | 'lg';

export const TheaterAmenityComponent = ({
  attribute,
  className,
  dataLinkCategory,
  size,
  textClassName,
  theater,
}: TheaterAmenityComponentProps): React.JSX.Element => {
  const { trackClick } = useDataAnalytics();
  const amenityName = attribute.displayName?.trim() || 'Theatre amenity';
  const amenitySlug = dataTestTransformer(amenityName);
  const isRestaurantMenu = attribute.brokerKey === 'AMEN_REST';
  const isAlcoholMenu = attribute.brokerKey === 'AMEN_AL';
  const hasDialogContent = !!attribute.description?.trim();
  const showDialog = isRestaurantMenu || isAlcoholMenu || hasDialogContent;

  const amenity = <div className={clsx('flex flex-col gap-sm w-full', !showDialog && className)}>
    <Image alt={attribute.altText?.trim() || `Theatre Amenity - ${amenityName}`}
      className={clsx('w-full rounded-md', theaterAmenityStyles['amenity-image'], {
        [theaterAmenityStyles['amenity-image-lg']]: size === 'lg',
        [theaterAmenityStyles['amenity-image-md']]: size === 'md',
        [theaterAmenityStyles['amenity-image-sm']]: size === 'sm',
      })}
      height={attribute.imageSmall?.height || 100}
      src={attribute.imageSmall?.url || '/img/star.svg'}
      width={attribute.imageSmall?.width || 100}
    />

    <p className={clsx('line-clamp-2 break-words font-semibold text-sm text-left', textClassName)}
      data-test={`amenity-name-${amenitySlug}`}
    >
      {amenityName}
    </p>
  </div>;

  if (showDialog) {
    return (
      <TheaterAmenityDialog attribute={attribute}
        ctaLink={isRestaurantMenu ? theater.restaurantMenuUrl : isAlcoholMenu ? theater.barMenuUrl : undefined}
        ctaText={isRestaurantMenu || isAlcoholMenu ? 'View Menu' : undefined}
        dataLinkName={isRestaurantMenu || isAlcoholMenu ? 'View Menu' : `${amenityName} Amenity`}
      >
        <button className={clsx('w-full', className)}
          data-test={`button-amenity-${amenitySlug}`}
          type="button"
          onClick={() => trackClick({ linkCategory: dataLinkCategory, linkName: `${amenityName} Amenity` })}
        >
          {amenity}
        </button>
      </TheaterAmenityDialog>
    );
  }


  return amenity;
};
